import { createContext, useContext, ReactNode } from "react";
import useSWR from "swr";
import { useCampaign } from "../views/Campaign/CampaignContext";
import { usePlace } from "./PlaceProvider";
import { useGoogleInsights } from "../views/GooglePerformance/GoogleInsightsProvider";
import { useReviewsConfig } from "../hooks/useReviewsConfig";
import { useSmsCampaigns } from "../hooks/useSmsCampaigns";
import { HTTPClient } from "../HTTPClient";
import dayjs from "dayjs";

interface ToDoContextValue {
  jeuDone: boolean;
  googleConnected: boolean | null;
  /** Automated AI replies to Google reviews are actually running. */
  reviewsAiDone: boolean | null;
  hasCampaigns: boolean | null;
  googleIaSetupDone: boolean | null;
  googleIaAnalysisDone: boolean | null;
  googleIaPrevMonthAvailable: boolean | null;
  googleIaCurrentMonthDone: boolean | null;
  prevMonthReady: boolean | null;
  pendingCount: number;
  totalDone: number;
  total: number;
  isLoading: boolean;
  refresh: () => void;
}

const ToDoContext = createContext<ToDoContextValue>({
  jeuDone: false,
  googleConnected: null,
  reviewsAiDone: null,
  hasCampaigns: null,
  googleIaSetupDone: null,
  googleIaAnalysisDone: null,
  googleIaPrevMonthAvailable: null,
  googleIaCurrentMonthDone: null,
  prevMonthReady: null,
  pendingCount: 0,
  totalDone: 2,
  total: 7,
  isLoading: true,
  refresh: () => {},
});

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function ToDoProvider({ children }: { children: ReactNode }) {
  const { isLoaded, actions, gifts } = useCampaign();
  const { selectedPlace } = usePlace();
  // GoogleInsightsProvider sits just above this one and already fetches
  // keyword-setup and keyword-insights/history. Reading them from its context
  // is what keeps this provider from issuing the same two GETs again — the
  // in-flight sharing in HTTPClient only catches them when the timing happens
  // to overlap, which it does not on the routes with a slow lazy chunk.
  const { history, historyLoaded, keywordSetupExists, keywordSetupLoaded, refreshHistory } = useGoogleInsights();
  const { config: reviewsConfig, loaded: reviewsConfigLoaded, refresh: refreshReviewsConfig } = useReviewsConfig();
  const { campaigns: smsCampaignList, loaded: smsCampaignsLoaded, refresh: refreshSmsCampaigns } = useSmsCampaigns();

  const jeuDone = isLoaded ? actions.length > 0 || gifts.length > 0 : false;
  const googleConnected = reviewsConfigLoaded ? !!reviewsConfig?.google : null;
  // Same condition the API answers on — see mustAnswer() in routes/reviews.js:
  // a reply style is required, disableAi switches it off.
  const reviewsAiDone = reviewsConfigLoaded ? !reviewsConfig?.disableAi && !!reviewsConfig?.style : null;
  const hasCampaigns = smsCampaignsLoaded ? smsCampaignList.length > 0 : null;
  // Every flag below now comes from a shared SWR key, so refreshing means
  // revalidating those keys — there is no local state left to bump.
  const refresh = () => {
    refreshHistory();
    refreshReviewsConfig();
    refreshSmsCampaigns();
  };

  const prevMonthKey = dayjs().subtract(1, "month").format("YYYY-MM");
  const currentMonthKey = dayjs().format("YYYY-MM");

  // null while the provider above is still loading — the to-do list renders a
  // spinner on null and a cross on false, so the two must stay distinct.
  const googleIaSetupDone = keywordSetupLoaded ? keywordSetupExists : null;
  const googleIaAnalysisDone = historyLoaded ? history.length > 0 : null;
  const googleIaPrevMonthAvailable = historyLoaded ? history.some((s) => s.month === prevMonthKey) : null;
  const googleIaCurrentMonthDone = historyLoaded ? history.some((s) => s.month === currentMonthKey) : null;

  // Readiness via SWR — clé incluant la date du jour → invalide automatiquement à minuit
  const readinessSWRKey = selectedPlace?.id
    ? `readiness:${selectedPlace.id}:${prevMonthKey}:${todayKey()}`
    : null;

  const { data: readinessData } = useSWR(
    readinessSWRKey,
    () => HTTPClient.get(`/stats/google/keyword-insights/readiness?placeId=${selectedPlace!.id}&monthKey=${prevMonthKey}`)
      .then(({ data }) => data?.ready === true)
      .catch(() => false),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  const prevMonthReady = readinessSWRKey ? (readinessData ?? null) : null;

  const isLoading = !isLoaded || googleConnected === null || hasCampaigns === null || googleIaSetupDone === null || googleIaAnalysisDone === null;

  const TOTAL_ACTIONS = 8;
  const doneCount = [
    true,
    true,
    jeuDone,
    googleConnected === true,
    hasCampaigns === true,
    googleIaSetupDone === true,
    googleIaAnalysisDone === true,
    googleIaPrevMonthAvailable === true,
  ].filter(Boolean).length;
  const pendingCount = TOTAL_ACTIONS - doneCount;

  const totalDone = doneCount;
  const total = TOTAL_ACTIONS;

  return (
    <ToDoContext.Provider value={{ jeuDone, googleConnected, reviewsAiDone, hasCampaigns, googleIaSetupDone, googleIaAnalysisDone, googleIaPrevMonthAvailable, googleIaCurrentMonthDone, prevMonthReady, pendingCount, totalDone, total, isLoading, refresh }}>
      {children}
    </ToDoContext.Provider>
  );
}

export function useToDo() {
  return useContext(ToDoContext);
}
